import React, { useMemo, useState } from 'react';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import {
  ABSENCE_REASONS,
  ABSENCE_STATUS,
  DAY_ARRIVE_LATE,
  DAY_FULL,
  DAY_LEAVE_EARLY,
  ENTRY_ABSENCE,
  PAY_TYPES_FOR_ABSENCE,
  computeNotice,
  describeEntry,
  formatRange,
  formatYmd,
  getDayType,
  isAbsence,
  isPartialDay,
  isoToDateTimeLocal,
  noticeBucket,
  nowDateTimeLocal,
  todayYmd,
  toYmd,
} from '../utils/timeOffUtils';
import ScopedPortalPopup from '../components/ScopedPortalPopup';

/* ---------------------------------------------------------------------------
   ATTENDANCE LOG  (rendered as the second tab of TimeOffAdmin)

   For things that already happened: someone called out, left early, or came
   in late. These live in the same `timeOff` collection as requests, flagged
   entryType: "absence", so the Calendar picks them up with no extra query.

   There is no approve / deny step - status is always "Recorded".
--------------------------------------------------------------------------- */

const PERIODS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const TYPE_FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'calledOut', label: 'Called out' },
  { value: 'leftEarly', label: 'Left early' },
  { value: 'late', label: 'Came in late' },
  { value: 'noCall', label: 'No call / no show' },
  { value: 'sameDay', label: 'Same-day notice' },
];

const blankForm = () => ({
  id: null,
  userId: '',
  dayType: DAY_FULL,
  startDate: todayYmd(),
  endDate: todayYmd(),
  partialTime: '',
  reasonCategory: 'Sick',
  reason: '',
  paidTimeType: 'Unpaid',
  notifiedAtLocal: nowDateTimeLocal(),
  noCallNoShow: false,
});

const matchesType = (entry, type) => {
  if (!type) return true;
  const day = getDayType(entry);
  if (type === 'calledOut') return day === DAY_FULL;
  if (type === 'leftEarly') return day === DAY_LEAVE_EARLY;
  if (type === 'late') return day === DAY_ARRIVE_LATE;
  if (type === 'noCall') return noticeBucket(entry) === 'noCall';
  if (type === 'sameDay') return noticeBucket(entry) === 'sameDay';
  return true;
};

const noticeChipClass = (entry) => {
  const b = noticeBucket(entry);
  if (b === 'noCall') return 'toa-chip toa-chip-denied';
  if (b === 'sameDay') return 'toa-chip toa-chip-pending';
  if (b === 'nightBefore' || b === 'ahead') return 'toa-chip toa-chip-approved';
  return 'toa-chip toa-chip-off';
};

const AttendanceLog = ({
  entries,
  users,
  activePeople,
  isForActivePerson,
  hideInactive,
  setHideInactive,
  currentUserName,
  onSaved,
  onOpenDetails,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [period, setPeriod] = useState('90');
  const [typeFilter, setTypeFilter] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  /* ----------------------------------------------------------- filtering -- */

  const periodStart = useMemo(() => {
    if (period === 'all') return '';
    const d = new Date();
    d.setDate(d.getDate() - Number(period));
    return toYmd(d);
  }, [period]);

  // Period + person filters only - used for the summary numbers so the type
  // filter narrows the log without changing the totals above it.
  const inPeriod = useMemo(
    () =>
      entries.filter((e) => {
        if (!isAbsence(e)) return false;
        if (hideInactive && !isForActivePerson(e)) return false;
        if (periodStart && String(e.endDate || e.startDate || '') < periodStart) return false;
        return true;
      }),
    [entries, hideInactive, isForActivePerson, periodStart]
  );

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return inPeriod
      .filter((e) => matchesType(e, typeFilter))
      .filter((e) => {
        if (!q) return true;
        return [e.name, e.reasonCategory, e.reason, e.noticeGiven, e.loggedBy]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || '')));
  }, [inPeriod, typeFilter, searchQuery]);

  const totals = useMemo(() => {
    const t = { calledOut: 0, leftEarly: 0, late: 0, noCall: 0, sameDay: 0 };
    inPeriod.forEach((e) => {
      ['calledOut', 'leftEarly', 'late', 'noCall', 'sameDay'].forEach((k) => {
        if (matchesType(e, k)) t[k] += 1;
      });
    });
    return t;
  }, [inPeriod]);

  // One row per person, most occurrences first.
  const byPerson = useMemo(() => {
    const map = new Map();
    inPeriod.forEach((e) => {
      const name = e.name || 'Unknown';
      if (!map.has(name)) {
        map.set(name, { name, calledOut: 0, leftEarly: 0, late: 0, noCall: 0, sameDay: 0, last: '' });
      }
      const row = map.get(name);
      ['calledOut', 'leftEarly', 'late', 'noCall', 'sameDay'].forEach((k) => {
        if (matchesType(e, k)) row[k] += 1;
      });
      if (String(e.startDate || '') > row.last) row.last = e.startDate;
    });
    return Array.from(map.values()).sort(
      (a, b) =>
        b.calledOut + b.leftEarly + b.late - (a.calledOut + a.leftEarly + a.late) ||
        a.name.localeCompare(b.name)
    );
  }, [inPeriod]);

  /* ---------------------------------------------------------------- form -- */

  const openNew = () => setForm(blankForm());

  const openEdit = (entry) => {
    const user = users.find(
      (u) =>
        u.id === entry.personUserId ||
        `${u.firstName || ''} ${u.lastName || ''}`.trim().toLowerCase() ===
          String(entry.name || '').trim().toLowerCase()
    );
    setForm({
      id: entry.id,
      userId: user ? user.id : '',
      fallbackName: entry.name || '',
      dayType: getDayType(entry),
      startDate: entry.startDate || todayYmd(),
      endDate: entry.endDate || entry.startDate || todayYmd(),
      partialTime: entry.partialTime || '',
      reasonCategory: entry.reasonCategory || 'Other',
      reason: entry.reason || '',
      paidTimeType: entry.paidTimeType || 'Unpaid',
      notifiedAtLocal: isoToDateTimeLocal(entry.notifiedAt) || nowDateTimeLocal(),
      noCallNoShow: Boolean(entry.noCallNoShow),
    });
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const notifiedIso = form && form.notifiedAtLocal ? new Date(form.notifiedAtLocal).toISOString() : '';
  const liveNotice = form
    ? computeNotice({
        notifiedAt: notifiedIso,
        startDate: form.startDate,
        noCallNoShow: form.noCallNoShow,
      })
    : '';

  const saveForm = async (e) => {
    e.preventDefault();
    if (!form) return;

    const user = users.find((u) => u.id === form.userId);
    const name = user
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : form.fallbackName || '';
    if (!name) {
      alert('Pick the person.');
      return;
    }
    const partial = form.dayType !== DAY_FULL;
    if (partial && !form.partialTime) {
      alert(form.dayType === DAY_LEAVE_EARLY ? 'Enter the time they left.' : 'Enter the time they got in.');
      return;
    }
    const endDate = partial ? form.startDate : form.endDate || form.startDate;
    if (endDate < form.startDate) {
      alert('The last day cannot be before the first day.');
      return;
    }

    const auth = getAuth();
    const me = auth.currentUser;

    const data = {
      entryType: ENTRY_ABSENCE,
      status: ABSENCE_STATUS,
      name,
      personUserId: user ? user.id : null,
      dayType: form.dayType,
      startDate: form.startDate,
      endDate,
      partialTime: partial ? form.partialTime : '',
      reasonCategory: form.reasonCategory,
      reason: form.reason.trim(),
      paidTimeType: form.paidTimeType,
      noCallNoShow: Boolean(form.noCallNoShow),
      notifiedAt: form.noCallNoShow ? null : notifiedIso || null,
      noticeGiven: liveNotice,
    };

    setSaving(true);
    try {
      if (form.id) {
        await updateDoc(doc(db, 'timeOff', form.id), {
          ...data,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUserName || '',
        });
        onSaved({ id: form.id, ...data }, 'updated');
      } else {
        const newData = {
          ...data,
          notes: [],
          loggedBy: currentUserName || '',
          loggedByUid: me ? me.uid : '',
          loggedAt: new Date().toISOString(),
          // Kept so existing "Submitted" columns and sorts still work.
          dateRequested: new Date().toISOString(),
          userId: me ? me.uid : '',
        };
        const ref = await addDoc(collection(db, 'timeOff'), newData);
        onSaved({ id: ref.id, ...newData }, 'created');
      }
      setForm(null);
    } catch (error) {
      console.error('Error saving attendance entry:', error);
      alert('Could not save this entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* -------------------------------------------------------------- render -- */

  return (
    <>
      <div className="toa-toolbar">
        <div className="toa-field toa-field-grow">
          <span className="toa-field-label" id="att-search-label">
            Search
          </span>
          <input
            aria-labelledby="att-search-label"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Name, reason, notice or who logged it"
          />
        </div>

        <div className="toa-field">
          <span className="toa-field-label" id="att-period-label">
            Period
          </span>
          <select
            aria-labelledby="att-period-label"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="toa-field">
          <span className="toa-field-label" id="att-type-label">
            Show
          </span>
          <select
            aria-labelledby="att-type-label"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {TYPE_FILTERS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <label className="toa-toggle">
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => setHideInactive(e.target.checked)}
          />
          Hide people no longer active
        </label>

        <button className="blue-button toa-log-button" onClick={openNew}>
          Log call-out / left early
        </button>
      </div>

      <div className="toa-stats toa-stats-tight">
        <div className="toa-stat toa-stat-denied">
          <span className="toa-stat-value">{totals.calledOut}</span>
          <span className="toa-stat-label">Called out</span>
        </div>
        <div className="toa-stat toa-stat-pending">
          <span className="toa-stat-value">{totals.leftEarly}</span>
          <span className="toa-stat-label">Left early</span>
        </div>
        <div className="toa-stat toa-stat-pending">
          <span className="toa-stat-value">{totals.late}</span>
          <span className="toa-stat-label">Came in late</span>
        </div>
        <div className="toa-stat toa-stat-black">
          <span className="toa-stat-value">{totals.noCall}</span>
          <span className="toa-stat-label">No call / no show</span>
        </div>
        <div className="toa-stat">
          <span className="toa-stat-value">{totals.sameDay}</span>
          <span className="toa-stat-label">Same-day notice</span>
        </div>
      </div>

      {byPerson.length > 0 && (
        <details className="toa-by-person" open>
          <summary>By person ({byPerson.length})</summary>
          <div className="toa-table-wrap toa-table-wrap-short">
            <table className="toa-table">
              <thead>
                <tr>
                  <th><span className="toa-th-plain">Person</span></th>
                  <th><span className="toa-th-plain">Called out</span></th>
                  <th><span className="toa-th-plain">Left early</span></th>
                  <th><span className="toa-th-plain">Late</span></th>
                  <th><span className="toa-th-plain">No call</span></th>
                  <th><span className="toa-th-plain">Same-day notice</span></th>
                  <th><span className="toa-th-plain">Most recent</span></th>
                </tr>
              </thead>
              <tbody>
                {byPerson.map((p) => (
                  <tr key={p.name}>
                    <td>
                      <button
                        type="button"
                        className="toa-link-button"
                        onClick={() => setSearchQuery(p.name)}
                        title="Show only this person"
                      >
                        {p.name}
                      </button>
                    </td>
                    <td>{p.calledOut || <span className="toa-muted">0</span>}</td>
                    <td>{p.leftEarly || <span className="toa-muted">0</span>}</td>
                    <td>{p.late || <span className="toa-muted">0</span>}</td>
                    <td>
                      {p.noCall ? (
                        <span className="toa-chip toa-chip-denied">{p.noCall}</span>
                      ) : (
                        <span className="toa-muted">0</span>
                      )}
                    </td>
                    <td>{p.sameDay || <span className="toa-muted">0</span>}</td>
                    <td>{formatYmd(p.last)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      <div className="toa-count toa-count-block">
        {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
      </div>

      <div className="toa-table-wrap">
        <table className="toa-table">
          <thead>
            <tr>
              <th><span className="toa-th-plain">Date</span></th>
              <th><span className="toa-th-plain">Person</span></th>
              <th><span className="toa-th-plain">What happened</span></th>
              <th><span className="toa-th-plain">Reason</span></th>
              <th><span className="toa-th-plain">Notice</span></th>
              <th><span className="toa-th-plain">Pay as</span></th>
              <th><span className="toa-th-plain">Logged by</span></th>
              <th className="toa-col-actions"><span className="toa-th-plain">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="toa-empty" colSpan={8}>
                  {inPeriod.length === 0
                    ? 'Nothing logged for this period. Use "Log call-out / left early" when someone calls in.'
                    : 'Nothing matches the current search and filters.'}
                </td>
              </tr>
            ) : (
              rows.map((e) => {
                const inactive = !isForActivePerson(e);
                return (
                  <tr key={e.id} className={inactive ? 'toa-row-inactive' : undefined}>
                    <td>
                      {isPartialDay(e) ? formatYmd(e.startDate) : formatRange(e.startDate, e.endDate)}
                    </td>
                    <td>
                      <span className="toa-strong">{e.name || 'Unknown'}</span>
                      {inactive && <span className="toa-sub">No longer active</span>}
                    </td>
                    <td>{describeEntry(e)}</td>
                    <td>
                      {e.reasonCategory || <span className="toa-muted">—</span>}
                      {e.reason && <span className="toa-sub">{e.reason}</span>}
                    </td>
                    <td>
                      {e.noticeGiven ? (
                        <span className={noticeChipClass(e)}>{e.noticeGiven}</span>
                      ) : (
                        <span className="toa-muted">—</span>
                      )}
                    </td>
                    <td>{e.paidTimeType || <span className="toa-muted">—</span>}</td>
                    <td>{e.loggedBy || <span className="toa-muted">—</span>}</td>
                    <td className="toa-col-actions">
                      <div className="button-group">
                        <button className="blue-button" onClick={() => onOpenDetails(e)}>
                          Details
                        </button>
                        <button className="grey-button" onClick={() => openEdit(e)}>
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ------------------------------------------------ Log / edit form -- */}
      {form && (
        <ScopedPortalPopup
          onClose={() => !saving && setForm(null)}
          scopeClass="time-off-admin-page toa-popup"
        >
            <h3>{form.id ? 'Edit attendance entry' : 'Log a call-out or left early'}</h3>
            <p className="toa-modal-lead">
              For something that already happened. It goes straight on the
              calendar - there is nothing to approve.
            </p>

            <form onSubmit={saveForm} style={{ display: 'contents' }}>
              <div className="toa-modal-scroll">
                <div className="toa-form-field">
                  <label htmlFor="att-person">Person</label>
                  <select
                    id="att-person"
                    value={form.userId}
                    onChange={(e) => setField('userId', e.target.value)}
                    required={!form.fallbackName}
                  >
                    <option value="">
                      {form.fallbackName ? `${form.fallbackName} (unchanged)` : 'Select a person'}
                    </option>
                    {activePeople.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="toa-form-field">
                  <span className="toa-radio-title">What happened?</span>
                  <div className="toa-radio-row">
                    {[
                      { value: DAY_FULL, label: 'Called out' },
                      { value: DAY_LEAVE_EARLY, label: 'Left early' },
                      { value: DAY_ARRIVE_LATE, label: 'Came in late' },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className={`toa-radio${form.dayType === opt.value ? ' toa-radio-on' : ''}`}
                      >
                        <input
                          type="radio"
                          name="att-daytype"
                          value={opt.value}
                          checked={form.dayType === opt.value}
                          onChange={() => setField('dayType', opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {form.dayType === DAY_FULL ? (
                  <div className="toa-form-row">
                    <div>
                      <label htmlFor="att-start">Day out</label>
                      <input
                        id="att-start"
                        type="date"
                        value={form.startDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            startDate: v,
                            endDate: !prev.endDate || prev.endDate < v ? v : prev.endDate,
                          }));
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="att-end">Through (if more than one day)</label>
                      <input
                        id="att-end"
                        type="date"
                        value={form.endDate}
                        min={form.startDate}
                        onChange={(e) => setField('endDate', e.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="toa-form-row">
                    <div>
                      <label htmlFor="att-start">Date</label>
                      <input
                        id="att-start"
                        type="date"
                        value={form.startDate}
                        onChange={(e) => setField('startDate', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="att-time">
                        {form.dayType === DAY_LEAVE_EARLY ? 'Left at' : 'Got in at'}
                      </label>
                      <input
                        id="att-time"
                        type="time"
                        value={form.partialTime}
                        onChange={(e) => setField('partialTime', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="toa-form-row">
                  <div>
                    <label htmlFor="att-reason-cat">Reason</label>
                    <select
                      id="att-reason-cat"
                      value={form.reasonCategory}
                      onChange={(e) => setField('reasonCategory', e.target.value)}
                    >
                      {ABSENCE_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="att-pay">Pay as</label>
                    <select
                      id="att-pay"
                      value={form.paidTimeType}
                      onChange={(e) => setField('paidTimeType', e.target.value)}
                    >
                      {PAY_TYPES_FOR_ABSENCE.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="toa-form-field">
                  <label htmlFor="att-reason">Details (optional)</label>
                  <textarea
                    id="att-reason"
                    value={form.reason}
                    onChange={(e) => setField('reason', e.target.value)}
                    placeholder="What they said"
                  />
                </div>

                <div className="toa-notice-box">
                  <label className="toa-toggle toa-toggle-inline">
                    <input
                      type="checkbox"
                      checked={form.noCallNoShow}
                      onChange={(e) => setField('noCallNoShow', e.target.checked)}
                    />
                    No call / no show - they never told anyone
                  </label>

                  {!form.noCallNoShow && (
                    <div className="toa-form-field" style={{ marginTop: 10, marginBottom: 0 }}>
                      <label htmlFor="att-notified">When did they let us know?</label>
                      <input
                        id="att-notified"
                        type="datetime-local"
                        value={form.notifiedAtLocal}
                        onChange={(e) => setField('notifiedAtLocal', e.target.value)}
                      />
                    </div>
                  )}

                  <p className="toa-notice-result">
                    Notice:{' '}
                    {liveNotice ? (
                      <span className={noticeChipClass({ noticeGiven: liveNotice, noCallNoShow: form.noCallNoShow })}>
                        {liveNotice}
                      </span>
                    ) : (
                      <span className="toa-muted">pick a date and time</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="button-group">
                <button type="submit" className="green-button" disabled={saving}>
                  {saving ? 'Saving...' : form.id ? 'Save changes' : 'Log it'}
                </button>
                <button
                  type="button"
                  className="grey-button"
                  onClick={() => setForm(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
        </ScopedPortalPopup>
      )}
    </>
  );
};

export default AttendanceLog;
